<template>
  <app-layout>
    <template #header>
      <AppHead :title="product.name" />
    </template>

    <div class="single-product pt-3">
      <flash-messages />

      <jet-button @click="goBack" btnStyle="secondary" class="mb-3" icon="fas fa-caret-left">Back to Products</jet-button>
      <section class="product-view bg-white p-3 px-lg-4 rounded">
        <h2 class="mb-3">{{ product.name }} <span v-if="product.variationData && current_product">{{ current_product.name.replace(product.name, '') }}</span></h2>
        <div class="row">
          <ThumbnailImages 
            class="col-12 col-xl-2" 
            :current_product="current_product" 
            @imageClicked="imageUrl => current_image = imageUrl"
          />
          <div class="col pl-3 mt-4 mt-xl-0">
            <div class="row">
              <div class="col-12 col-md-8">
                <img :src="current_image" :alt="product.sku" class="mb-3 main-image" />
              </div>
              <div class="col-12 col-md-4 d-block d-lg-flex justify-content-end">
                <div>
                  <div class="bg-light m-w-100 product-cart-control border rounded p-3">
                    <div class="row">
                      <div class="col-12">
                        <p class="m-0 font-weight-bold">{{ cart_product.sku }}</p>
                      </div>
                    </div>
                    <div>
                      <PriceDisplay priceSize="20px" :product="product"/>
                    </div>
                    <div>
                      <ProductQty
                        ref="product-qty"
                        :product="cart_product"
                        :class="{disabled: !cart_product}"
                        :cart_items="$page.props.auth.user.cart_items"
                        :routes="routes"
                        @updateCartItems="updateCartItems"
                      />
                    </div>
                    <div class="text-size-small text-secondary product-info mt-2 mt-md-0">
                      <p v-if="current_product.inventory > 0" class="mb-1">Available on Truck: <strong>{{ current_product.inventory }}</strong></p>
                      <div v-if="product.user_id">
                        <p v-if="current_product.inventory > 0" class="mb-1">Only <strong>{{ current_product.inventory }}</strong> may be ordered.</p>
                        <p v-else class="mb-1"><em>Not Available</em></p>
                      </div>
                      <div v-else>
                        <p class="m-0 mb-1" v-if="current_product.current_inventory > 0">
                          MTN stock: <strong>{{ current_product.current_inventory >= 20 ? '20+' : current_product.current_inventory }}</strong>
                          <span v-if="!product.backorder_enabled_tu && product.current_inventory < 20" class="d-block my-1">Only <strong>{{ product.inventory + current_product.current_inventory }}</strong> may be ordered.</span>
                        </p>
                        <p v-else-if="product.backorder_enabled_tu" class="mb-1">Ships in {{ product.backorder_eta }} days</p>
                        <div v-else>
                          <p class="mb-1" v-if="product.inventory > 0">Only <strong>{{ product.inventory }}</strong> may be ordered.</p>
                          <p class="mb-1" v-else>Not Available</p>
                        </div>
                      </div>

                      <Variations
                        ref="variations"
                        :product="product"
                        :attributes="attributes"
                        class="mt-3"
                        @refresh="refresh"
                        @setCartProduct="val => cart_product = val"
                        @setCurrentImage="val => current_image = val"
                        @setCurrentProduct="val => current_product = val"
                        @setDefaultProduct="val => default_product = val"
                      />

                      <VariationCartItems
                        ref="variation-cart-items"
                        :cart_items="$page.props.auth.user.cart_items"
                        :product="product"
                        :isSingle="true"
                        @fillOption="fillOption"
                      />

                    </div>
                    <SingleBundles 
                      v-if="$page.props.has_single_bundles"
                      :routes="routes"
                      :cart_items="$page.props.auth.user.cart_items"
                      class="pt-4"
                      :original_product="product"
                    />
                  </div>

                  <div v-if="product.pdfs.length" class="bg-light w-100 product-cart-control border rounded px-3 pb-3 pt-2 mt-3">
                    <p class="text-dark font-weight-bold">Additional Item Info</p>
                    <PdfList :pdfs="product.pdfs" />
                  </div>

                  <div v-if="product.sales_video_url && getYoutubeId(product.sales_video_url)">
                    <div class="embed-responsive embed-responsive-16by9 mt-4">
                      <iframe v-if="product.sales_video_url" title="Sales Video"
                        :src="`//www.youtube.com/embed/${getYoutubeId(product.sales_video_url)}`"
                        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen></iframe>
                    </div>
                    <p>
                      {{product.sales_video_description}}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-3 mt-lg-0 pr-0 pr-lg-5">
              <p class="m-0 font-weight-bold">{{product.sku}}</p>
              <p class="font-italic mb-2">{{product.short_description}}</p>
              <ExpandSection v-if="product.long_description  && $page.props.is_bundle" :elemHeight="150">
                <div class="text-secondary lh-small m-0" v-html="product.long_description" />
              </ExpandSection>

              <div v-else class="text-secondary lh-small m-0" v-html="product.long_description" />

              <WarrantyInformation v-if="warranty" :warranty="warranty" />

              <BundledProducts 
                v-if="$page.props.product.bundled_products"
                class="mt-4"
                productRoute="products.view"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  </app-layout>
</template>

<script>
  import AppLayout from '@/Layouts/AppLayout'
  import FlashMessages from '@/Shared/FlashMessages'
  import ExpandSection from '@/Shared/ExpandSection'
  import ProductQty from '@/Components/Shop/ProductQty'
  import BundledProducts from '@/Components/Shop/BundledProducts'
  import SingleBundles from '@/Components/Shop/SingleBundles'
  import PriceDisplay from '@/Components/Shop/PriceDisplay'
  import Variations from '@/Components/Shop/Variations'
  import ThumbnailImages from '@/Components/Shop/ThumbnailImages'
  import VariationCartItems from '@/Components/Shop/VariationCartItems'
  import PdfList from '@/Components/Shop/PdfList'
  import WarrantyInformation from '@/Components/Products/WarrantyInformation.vue'

  export default {
    components: {
      AppLayout,
      FlashMessages,
      ProductQty,
      BundledProducts,
      SingleBundles,
      ExpandSection,
      PriceDisplay,
      Variations,
      ThumbnailImages,
      VariationCartItems,
      WarrantyInformation,
      PdfList
    },
    props: {
      product: Object,
      warranty: Object,
      images: Object,
      user: Object,
    },
    data() {
      return {
        current_product: this.product,
        default_product: this.product,
        cart_product: this.product,
        current_image: this.product.image_url,
        attributes: this.product.variationData ? this.product.variationData.attributes : null,
        routes: {
          add: 'cart.add',
          update: 'cart.updateProduct',
          delete: 'cart.deleteProduct',
          category: 'mtd.shop.category'
        }
      }
    },
    mounted() {
      this.preloadShopImages()
    },
    methods: {
      getYoutubeId(url) {
        let id = null
        if (url)
        {
          let baseUrl = null;
          let endChar = null;

          if(url.startsWith('https://www.youtube.com/watch?v=')) {
            baseUrl = 'https://www.youtube.com/watch?v=';
            endChar = '&';
          }
          else if (url.startsWith('https://youtu.be/')) {
            baseUrl = 'https://youtu.be/';
            endChar = '?';
          }

          if (baseUrl) {
            id = url.replace(baseUrl, '')
            let endPosition = id.indexOf(endChar)
            if (endPosition != -1) {
              id = id.substring(0, endPosition)
            }
          }
        }
        return id
      },
      updateCartItems() {
        this.$refs['variation-cart-items'].getCartItems()
      },
      refresh() {
        this.$refs['product-qty'].refresh()
      },
      goBack() {
        history.back()
      },
      fillOption(product, optionId, option) {
        // Find product based on cart item's id and set attributes to the found product's attributes

        this.product.variationData.products.forEach(variationProduct => {
          if (option.product.id === variationProduct.id) {
            variationProduct.values.forEach( val => {
              this.attributes.forEach(attribute => {
                if (attribute.attribute_id === val.attribute_id) attribute.selected_value_id = val.value_id
              })
            })
          }
        })

        this.$refs.variations.setAttributeValue()
      },
      preloadShopImages() {
        if (this.product.images.length) {
          this.product.images.forEach(img => new Image().src = img.url)
        }
      }
    }
  }
</script>
